import React, { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useAuthContext } from './context/AuthContext';
import { evalRoutes } from './eval/routes';
import { ForgeLayout } from './forge/components/layout/ForgeLayout';
import {
  FORGE_MASTER_LABEL_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_UNRESOLVED_TASK_LIST_PATH,
} from './forge/paths';
import { forgeRoutes } from './forge/routes';
import { fetchRecognitionReviewAccess } from './forge/services/ForgeApiService';
import { loadRouteChunk } from './forge/utils/lazyRouteModule';
import { CandidateDetailPage } from './pages/embeddings/CandidateDetailPage';
import { CandidatesPage } from './pages/embeddings/CandidatesPage';
import EmbeddingDetailPage from './pages/embeddings/EmbeddingDetailPage';
import { EmbeddingsPage } from './pages/embeddings/EmbeddingsPage';
import { OutsSelectionPage } from './pages/embeddings/OutsSelectionPage';
import { GroundTruthAuditPage } from './pages/ground-truth/GroundTruthAuditPage';
import { ModEligibilityPage } from './pages/mod-eligibility/ModEligibilityPage';
import { PogResetInstallMapPage } from './pages/pog-reset/PogResetInstallMapPage';
import { PogResetModsPage } from './pages/pog-reset/PogResetModsPage';
import { PogResetPage } from './pages/pog-reset/PogResetPage';
import { PogResetStatsPage } from './pages/pog-reset/PogResetStatsPage';
import { PogResetTaskDetailPage } from './pages/pog-reset/PogResetTaskDetailPage';
import { StoreDetailsPage } from './pages/store-details/StoreDetailsPage';
import StoresPage from './pages/stores/StoresPage';
import { ImageAuditPageWrapper } from './pages/tasks/ImageAuditPageWrapper';
import { TaskAuditPageWrapper } from './pages/tasks/TaskDetailPageWrapper';
import { TasksPage } from './pages/tasks/TasksPage';

export function recognitionLandingPath(
  hasInternalAccess: boolean,
  hasRecognitionAccess: boolean,
  internalPath: string
): string {
  if (hasInternalAccess) return internalPath;
  return hasRecognitionAccess ? FORGE_RECOGNITION_OKRS_PATH : '/embeddings/detail';
}

const RecognitionAwareRedirect: React.FC<{ internalPath: string }> = ({ internalPath }) => {
  const { token, hasInternalAccess } = useAuthContext();
  const [recognitionPath, setRecognitionPath] = useState<string | null>(null);

  useEffect(() => {
    if (hasInternalAccess || !token) return undefined;
    let active = true;
    setRecognitionPath(null);
    void fetchRecognitionReviewAccess(token)
      .then((response) => {
        if (active) {
          setRecognitionPath(recognitionLandingPath(false, response.hasAccess, internalPath));
        }
      })
      .catch(() => {
        if (active) setRecognitionPath(recognitionLandingPath(false, false, internalPath));
      });
    return () => {
      active = false;
    };
  }, [hasInternalAccess, internalPath, token]);

  if (hasInternalAccess) {
    return <Navigate to={recognitionLandingPath(true, false, internalPath)} replace />;
  }
  if (!token) return <Navigate to="/embeddings/detail" replace />;
  return recognitionPath ? <Navigate to={recognitionPath} replace /> : null;
};

const RootRedirect: React.FC = () => <RecognitionAwareRedirect internalPath="/stores" />;

type RouteCapability = 'hasInternalAccess' | 'hasRemoteDataCollectionAccess';

const ProtectedRoute: React.FC<{
  element: React.ReactElement;
  capability?: RouteCapability;
}> = ({ element, capability = 'hasInternalAccess' }) => {
  const auth = useAuthContext();

  if (!auth[capability]) {
    return <Navigate to="/embeddings/detail" replace />;
  }

  return element;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/stores',
    element: <ProtectedRoute element={<StoresPage />} />,
  },
  {
    path: '/stores/:storeName/mod-eligibility',
    element: <ProtectedRoute element={<ModEligibilityPage />} />,
  },
  {
    path: '/pog-reset',
    element: <ProtectedRoute element={<PogResetPage />} />,
  },
  {
    path: '/stores/:storeName/pog-reset',
    element: <ProtectedRoute element={<PogResetPage />} />,
  },
  {
    // Before :taskId so 'mods' isn't captured as a task id.
    path: '/stores/:storeName/pog-reset/mods',
    element: <ProtectedRoute element={<PogResetModsPage />} />,
  },
  {
    path: '/stores/:storeName/pog-reset/install-map',
    element: <ProtectedRoute element={<PogResetInstallMapPage />} />,
  },
  {
    path: '/stores/:storeName/pog-reset/stats',
    element: <ProtectedRoute element={<PogResetStatsPage />} />,
  },
  {
    path: '/stores/:storeName/pog-reset/:taskId',
    element: <ProtectedRoute element={<PogResetTaskDetailPage />} />,
  },
  {
    path: '/stores/:storeName/:deviceName?',
    element: <ProtectedRoute element={<StoreDetailsPage />} />,
  },
  {
    path: '/embeddings',
    element: <ProtectedRoute element={<EmbeddingsPage />} />,
  },
  {
    path: '/embeddings/detail',
    element: <EmbeddingDetailPage />,
  },
  {
    path: '/tasks',
    element: <ProtectedRoute element={<TasksPage />} />,
  },
  {
    path: '/tasks/:taskId',
    element: <ProtectedRoute element={<TaskAuditPageWrapper />} />,
  },
  {
    path: '/gt/audit/:gtSampleId',
    element: <ProtectedRoute element={<GroundTruthAuditPage />} />,
  },
  {
    path: '/images/:imageId/audit',
    element: <ProtectedRoute element={<ImageAuditPageWrapper />} />,
  },
  {
    path: '/embeddings/candidates',
    element: <ProtectedRoute element={<CandidatesPage />} />,
  },
  {
    path: '/embeddings/candidates/:imageId',
    element: <CandidateDetailPage />,
  },
  {
    path: '/forge',
    element: <RecognitionAwareRedirect internalPath={FORGE_SYSTEM_HEALTH_PATH} />,
  },
  {
    path: FORGE_MASTER_LABEL_PATH,
    element: <ForgeLayout />,
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (await loadRouteChunk(() => import('./forge/pages/admin/MasterLabelPage')))
            .MasterLabelPage,
        }),
      },
    ],
  },
  {
    path: FORGE_REMOTE_DC_TOOL_PATH,
    element: (
      <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<ForgeLayout />} />
    ),
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (
            await loadRouteChunk(() => import('./forge/pages/admin/MasterDataCollectionPage'))
          ).MasterDataCollectionPage,
        }),
      },
    ],
  },
  {
    path: FORGE_REMOTE_DC_TASK_LIST_PATH,
    element: (
      <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<ForgeLayout />} />
    ),
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (
            await loadRouteChunk(
              () => import('./forge/pages/dashboard/RemoteDataCollectionDashboard')
            )
          ).RemoteDataCollectionDashboard,
        }),
      },
    ],
  },
  {
    path: FORGE_UNRESOLVED_TASK_LIST_PATH,
    element: <ForgeLayout />,
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (
            await loadRouteChunk(
              () => import('./forge/pages/dashboard/MasterLabelUnresolvedDashboard')
            )
          ).MasterLabelUnresolvedDashboard,
        }),
      },
    ],
  },
  {
    path: FORGE_RECOGNITION_OKRS_PATH,
    element: <ForgeLayout />,
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (
            await loadRouteChunk(() => import('./forge/pages/dashboard/RecognitionOkrsDashboard'))
          ).RecognitionOkrsDashboard,
        }),
      },
    ],
  },
  {
    path: FORGE_PR_REVIEW_PATH,
    element: <ForgeLayout />,
    children: [
      {
        index: true,
        lazy: async () => ({
          Component: (
            await loadRouteChunk(() => import('./forge/pages/admin/RecognitionReviewToolPage'))
          ).RecognitionReviewToolPage,
        }),
      },
    ],
  },
  {
    ...forgeRoutes,
    element: <ProtectedRoute element={forgeRoutes.element as React.ReactElement} />,
  },
  {
    ...evalRoutes,
    element: <ProtectedRoute element={evalRoutes.element as React.ReactElement} />,
  },
  {
    path: '/outs-selection/:imageId',
    element: <OutsSelectionPage />,
  },
]);

export const App: React.FC = () => {
  return <RouterProvider router={router} />;
};
